import { useEffect, useRef, useState } from "react";
import { Maximize2, Minimize2, ZoomIn, ZoomOut, Scan } from "lucide-react";
import { Button } from "./ui/button";

// Coordinate utente SVG (rispetta viewBox, zoom e letterbox) da un evento puntatore.
export function svgPoint(svg, e) {
  if (!svg) return null;
  const ctm = svg.getScreenCTM(); if (!ctm) return null;
  const pt = svg.createSVGPoint(); pt.x = e.clientX; pt.y = e.clientY;
  const p = pt.matrixTransform(ctm.inverse());
  return { x: p.x, y: p.y };
}

// Schermo intero + zoom (rotella / pizzico / pulsanti) + pan (tasto centrale, Alt+trascina, due dita).
export function useEditorViewport(svgRef, W, H, startFullscreen = false) {
  const [fs, setFs] = useState(startFullscreen);
  const [vb, setVb] = useState({ x: 0, y: 0, w: W, h: H });
  const pan = useRef(null);
  const touches = useRef(new Map());
  useEffect(() => { setVb({ x: 0, y: 0, w: W, h: H }); }, [W, H]);
  useEffect(() => {
    if (!fs) return undefined;
    const k = (e) => { if (e.key === "Escape") setFs(false); };
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", k);
    return () => { window.removeEventListener("keydown", k); document.body.style.overflow = ""; };
  }, [fs]);

  const zoomAt = (f, cx, cy) => setVb((v) => {
    const w = Math.min(W, Math.max(W / 20, v.w * f)), h = (w * H) / W;
    const px = cx ?? v.x + v.w / 2, py = cy ?? v.y + v.h / 2;
    return { x: px - ((px - v.x) * w) / v.w, y: py - ((py - v.y) * h) / v.h, w, h };
  });
  useEffect(() => {
    const el = svgRef.current; if (!el) return undefined;
    const onWheel = (e) => { if (!fs && !e.ctrlKey) return; e.preventDefault(); const p = svgPoint(el, e); zoomAt(e.deltaY > 0 ? 1.15 : 1 / 1.15, p?.x, p?.y); };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  });

  // true = evento consumato dal pan/pizzico
  const onDown = (e) => {
    if (e.pointerType === "touch") touches.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (e.button === 1 || e.altKey || touches.current.size >= 2) {
      e.preventDefault(); e.stopPropagation();
      pan.current = { cx: e.clientX, cy: e.clientY, vb, dist: null };
      return true;
    }
    return false;
  };
  const onMove = (e) => {
    if (e.pointerType === "touch" && touches.current.has(e.pointerId)) touches.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    const p = pan.current; if (!p) return false;
    const r = svgRef.current.getBoundingClientRect();
    const sc = p.vb.w / r.width;
    if (touches.current.size >= 2) {
      const [a, b] = [...touches.current.values()];
      const d = Math.hypot(a.x - b.x, a.y - b.y);
      if (p.dist == null) { p.dist = d; p.cx = (a.x + b.x) / 2; p.cy = (a.y + b.y) / 2; p.vb = vb; return true; }
      const f = p.dist / d, w = Math.min(W, Math.max(W / 20, p.vb.w * f)), h = (w * H) / W;
      const mx = (a.x + b.x) / 2, my = (a.y + b.y) / 2;
      setVb({ x: p.vb.x + (p.vb.w - w) / 2 - (mx - p.cx) * sc, y: p.vb.y + (p.vb.h - h) / 2 - (my - p.cy) * sc, w, h });
      return true;
    }
    setVb({ ...p.vb, x: p.vb.x - (e.clientX - p.cx) * sc, y: p.vb.y - (e.clientY - p.cy) * sc });
    return true;
  };
  const onUp = (e) => {
    if (e?.pointerType === "touch") touches.current.delete(e.pointerId);
    if (pan.current && touches.current.size < 2) { pan.current = null; return true; }
    return false;
  };

  const viewBox = `${vb.x} ${vb.y} ${vb.w} ${vb.h}`;
  const wrapClass = fs ? "fixed inset-0 z-[60] bg-background p-3 flex flex-col gap-2 overflow-hidden" : "";
  const svgClass = fs ? "w-full h-full flex-1 min-h-0 block select-none touch-none" : "w-full block select-none touch-none";
  return { fs, setFs, vb, viewBox, wrapClass, svgClass, zoomAt, reset: () => setVb({ x: 0, y: 0, w: W, h: H }), onDown, onMove, onUp };
}

export function ViewportToolbar({ v, prefix }) {
  return (
    <div className="flex items-center gap-1">
      <Button size="sm" variant="outline" onClick={() => v.zoomAt(1 / 1.3)} data-testid={`${prefix}-zoom-in`} title="Zoom +"><ZoomIn className="h-4 w-4" /></Button>
      <Button size="sm" variant="outline" onClick={() => v.zoomAt(1.3)} data-testid={`${prefix}-zoom-out`} title="Zoom −"><ZoomOut className="h-4 w-4" /></Button>
      <Button size="sm" variant="outline" onClick={v.reset} data-testid={`${prefix}-zoom-reset`} title="Vista intera"><Scan className="h-4 w-4" /></Button>
      <Button size="sm" variant={v.fs ? "default" : "outline"} onClick={() => v.setFs((x) => !x)} data-testid={`${prefix}-fullscreen`}>
        {v.fs ? <Minimize2 className="h-4 w-4 mr-1" /> : <Maximize2 className="h-4 w-4 mr-1" />}{v.fs ? "Esci" : "Schermo intero"}
      </Button>
    </div>
  );
}
